import type {ReactElement} from 'react';
import {useEffect, useState} from 'react';
import {Dropdown} from '@Components/dropdown-v2';
import {ALIGNMENT_TYPE, DROPDOWN_POSITION} from '@Components/dropdown-v2/dropdown.types';
import {Button, Size, Type} from '@Components/button';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import {ClickableDiv} from '@Components/clickable-div';
import {MOBILE_MODAL_WIDTH_THRESHOLD} from '@Components/modal';
import {MODALS_IDS} from '@Components/modal-container';
import {closeModal} from '@Components/modal-container/modal-container-reducer';
import useWindowSize from '@Hooks/useWindowSize';
import {useAppDispatch, useAppSelector} from '@/hooks';
import {StockPanelTabs} from '@Panels/stock-panel/stock-panel.types';
import type {AiDropdownItem, AiPanelKey, StockAiButtonProps} from './stock-ai-button.types';
import {AI_TOOLS_MODAL_PANEL_ID, TAB_TO_AI_PANEL_KEY} from './stock-ai-button.types';
import styles from './stock-ai-button.module.scss';

export function StockAiButton(props: StockAiButtonProps): ReactElement | null {
  const [isOpen, setIsOpen] = useState(false);
  const dispatch = useAppDispatch();
  const {windowWidth} = useWindowSize();
  const isSmallScreen = windowWidth < MOBILE_MODAL_WIDTH_THRESHOLD;

  const isAiToolsModalShowing = useAppSelector((state): boolean => {
    const modal = state.modals.modalsHashmap[MODALS_IDS.MORE_OPTIONS_MODAL];
    if (!modal?.isOpen) {
      return false;
    }
    return modal.panelProps?.panelId === AI_TOOLS_MODAL_PANEL_ID;
  });

  // Dismissing the small-screen modal without picking (outside tap / swipe) closes it through the modal system,
  // which never fires the Dropdown's onClose — so the open state must be cleared from the modal's Redux state.
  useEffect(() => {
    if (isSmallScreen && isOpen && !isAiToolsModalShowing) {
      setIsOpen(false);
    }
  }, [isSmallScreen, isOpen, isAiToolsModalShowing]);

  // Below MOBILE_MODAL_WIDTH_THRESHOLD the Dropdown renders its popup inside a MoreOptionsModal. That modal is opened
  // imperatively, so the controlled `open` state only closes the desktop popup — the modal must be closed explicitly.
  const closeSmallScreenAiToolsModal = (): void => {
    if (isSmallScreen) {
      dispatch(closeModal(MODALS_IDS.MORE_OPTIONS_MODAL));
    }
  };

  const getDropdownItems = (): AiDropdownItem[] => {
    return [
      {id: 'ai-images', icon: 'icon-ai-image', dropdownLabelKey: 'pmwjs_ai_images', ctaLabelKey: 'pmwjs_create_ai_images', onClick: props.onAiImages},
      {id: 'ai-stickers', icon: 'icon-ai-sticker', dropdownLabelKey: 'pmwjs_ai_stickers_caps', ctaLabelKey: 'pmwjs_create_ai_stickers', onClick: props.onAiStickers},
      {id: 'ai-videos', icon: 'icon-video-ai', dropdownLabelKey: 'pmwjs_ai_videos', ctaLabelKey: 'pmwjs_create_ai_videos', onClick: props.onAiVideos},
      {id: 'ai-voice', icon: 'icon-ai-voice', dropdownLabelKey: 'pmwjs_ai_voice', ctaLabelKey: 'pmwjs_create_ai_voice', onClick: props.onAiVoice},
    ];
  };

  const getTabItem = (): AiDropdownItem | undefined => {
    const items = getDropdownItems();
    switch (props.selectedTab) {
      case StockPanelTabs.PHOTOS:
        return items.find((i) => i.id === 'ai-images');
      case StockPanelTabs.STICKERS:
        return items.find((i) => i.id === 'ai-stickers');
      case StockPanelTabs.VIDEOS:
        return items.find((i) => i.id === 'ai-videos');
      case StockPanelTabs.AUDIOS:
        return items.find((i) => i.id === 'ai-voice');
      default:
        return undefined;
    }
  };

  const getRelevantDropdownItems = (): AiDropdownItem[] => {
    const relevantKeys = new Set<AiPanelKey>();
    props.tabsToShow.forEach((tab): void => {
      const key = TAB_TO_AI_PANEL_KEY[tab];
      if (key) {
        relevantKeys.add(key);
      }
    });
    return getDropdownItems().filter((item): boolean => relevantKeys.has(item.id));
  };

  const getDropdownPopup = (items: AiDropdownItem[]): ReactElement => {
    return (
      <ul className={'flex-v-row gap-2 spacing-m-0 spacing-p-0'}>
        {items.map((item): ReactElement => {
          return (
            <li key={item.id}>
              <ClickableDiv
                className={`flex-h-row flex-center-v ${styles.dropdownItem}`}
                onClick={(): void => {
                  setIsOpen(false);
                  closeSmallScreenAiToolsModal();
                  item.onClick();
                }}
              >
                <Icon icon={item.icon} size={IconSize.SIZE_ICON_20} className="spacing-m-r-3" />
                <Text val={window.i18next.t(item.dropdownLabelKey)} size={TextSize.SMALL} />
              </ClickableDiv>
            </li>
          );
        })}
      </ul>
    );
  };

  const isFullWidth = (props.widthMode ?? 'full') === 'full';
  const wrapperClass = isFullWidth ? 'spacing-m-l-4 spacing-m-r-4 spacing-m-t-2 spacing-m-b-6' : '';
  const buttonClass = isFullWidth ? `${styles.aiButton} _full-width` : styles.aiButton;

  const getButton = (label: string, icon: string, onClick?: () => void): ReactElement => {
    return <Button text={label} icon={icon} type={Type.PRIMARY} size={Size.SMALL} isFullWidth={isFullWidth} customClasses={buttonClass} onClick={onClick} />;
  };

  if (props.isGridView) {
    const tabItem = getTabItem();
    if (!tabItem) {
      return <div className={wrapperClass}>{getButton(window.i18next.t('pmwjs_create_with_ai'), 'icon-sparkle')}</div>;
    }
    return <div className={wrapperClass}>{getButton(window.i18next.t(tabItem.ctaLabelKey), tabItem.icon, tabItem.onClick)}</div>;
  }

  const relevantItems = getRelevantDropdownItems();
  if (relevantItems.length === 0) {
    return null;
  }
  if (relevantItems.length === 1) {
    const item = relevantItems[0];
    return (
      <div className={wrapperClass}>
        {getButton(window.i18next.t(item.ctaLabelKey), item.icon, (): void => {
          item.onClick();
        })}
      </div>
    );
  }

  return (
    <div className={wrapperClass}>
      <Dropdown
        open={isOpen}
        onOpen={(): void => {
          setIsOpen(true);
        }}
        onClose={(): void => {
          setIsOpen(false);
        }}
        doCloseOnInnerClick={false}
        alignment={ALIGNMENT_TYPE.BOTTOM_END}
        popUpHasCustomWidth
        popupClassName={styles.dropdownPopup}
        popupSpacingClass="spacing-p-2"
        position={DROPDOWN_POSITION.FIXED}
        showModalForSmallScreen
        panelIdForModal={AI_TOOLS_MODAL_PANEL_ID}
        modalTitle={window.i18next.t('pmwjs_create_with_ai')}
        modalHeaderClasses={styles.aiToolsModalHeader}
        selector={getButton(window.i18next.t('pmwjs_create_with_ai'), 'icon-sparkle')}
        popup={getDropdownPopup(relevantItems)}
      />
    </div>
  );
}
